// src/excel/builders/geo/buildWorkbook.js
// XLSX-билдер для сервиса GEO

export function buildGeoWorkbook(XLSX, params) {
  const {
    t,
    shortcode,
    stationName,
    startDate,
    endDate,
    rows = [],
    total = 0,
    nogeo = 0,
    fmtNumber,
    locale,
    serviceName
  } = params || {};

  const wb = XLSX.utils.book_new();
  const ws = {};
  const merges = [];

  const brandText = (t && t('listeners.brand_line_excel')) || '🔊 Radiomonitor.Net';
  const serviceTitle = serviceName || (t ? t('geo.title') : 'Geography report');
  const stationTitle = stationName || shortcode || '';
  const periodFrom = `${t ? t('geo.from') : 'From'}: ${startDate || ''}`;
  const periodTo = `${t ? t('geo.to') : 'To'}: ${endDate || ''}`;
  const madeOnText = (t && t('pdf.made_on')) || 'Report made on Radiomonitor.net';

  let currentRow = 0;

  // Брендовая строка
  XLSX.utils.sheet_add_aoa(ws, [[brandText]], { origin: { r: currentRow, c: 0 } });
  merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 1 } });
  const brandAddr = XLSX.utils.encode_cell({ r: currentRow, c: 0 });
  ws[brandAddr] = ws[brandAddr] || {};
  ws[brandAddr].s = {
    font: { bold: true, sz: 20, color: { rgb: '1F2A5A' } },
    fill: { fgColor: { rgb: 'EEF2FF' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };
  currentRow += 2;

  // Мета информация
  const metaRows = [
    [serviceTitle],
    ...(stationTitle ? [[stationTitle]] : []),
    [periodFrom],
    [periodTo],
    [madeOnText],
    ['']
  ];
  XLSX.utils.sheet_add_aoa(ws, metaRows, { origin: { r: currentRow, c: 0 } });
  for (let i = 0; i < metaRows.length - 1; i += 1) {
    merges.push({ s: { r: currentRow + i, c: 0 }, e: { r: currentRow + i, c: 1 } });
  }
  const encode = XLSX.utils.encode_cell;
  const serviceAddr = encode({ r: currentRow, c: 0 });
  if (ws[serviceAddr]) {
    ws[serviceAddr].s = {
      font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  if (stationTitle) {
    const stationAddr = encode({ r: currentRow + 1, c: 0 });
    if (ws[stationAddr]) {
      ws[stationAddr].s = {
        font: { bold: true, sz: 18, color: { rgb: '1F1F1F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }

  const infoStart = currentRow + 1 + (stationTitle ? 1 : 0);
  for (let i = 0; i < 2; i += 1) {
    const addr = encode({ r: infoStart + i, c: 0 });
    if (ws[addr]) {
      ws[addr].s = {
        font: { sz: 11, color: { rgb: '4F4F4F' } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    }
  }
  const madeOnAddr = encode({ r: infoStart + 2, c: 0 });
  if (ws[madeOnAddr]) {
    ws[madeOnAddr].s = {
      font: { sz: 10, color: { rgb: '4F4F4F' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    };
  }

  currentRow += metaRows.length;

  const HEAD_COUNTRY = (t ? t('geo.table.country') : 'Country');
  const HEAD_LISTENERS = (t ? t('geo.table.listeners') : 'Listeners');

  const countryRows = rows
    .filter((row) => row && row.country && row.country !== 'Без географії')
    .map((row) => [
      row.country,
      typeof row.listeners === 'number' ? row.listeners : Number(row.listeners || 0)
    ]);

  // Строка "Без географії"
  countryRows.push([
    t ? t('geo.unknown') : 'Unknown geography',
    typeof nogeo === 'number' ? nogeo : Number(nogeo || 0)
  ]);

  const totalsValue = typeof total === 'number'
    ? total
    : countryRows.reduce((acc, [, val]) => acc + (Number(val) || 0), 0);
  const formattedTotal = fmtNumber ? fmtNumber(totalsValue, locale) : totalsValue;

  const tableBlock = [
    [HEAD_COUNTRY, HEAD_LISTENERS],
    ...countryRows,
    ['', totalsValue]
  ];

  XLSX.utils.sheet_add_aoa(ws, tableBlock, { origin: { r: currentRow, c: 0 } });

  const headerRowIndex = currentRow;
  const totalRowIndex = currentRow + tableBlock.length - 1;

  const headerStyle = {
    font: { bold: true, color: { rgb: '333333' } },
    fill: { fgColor: { rgb: 'F0F0F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: 'D0D0D0' } },
      bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
      left: { style: 'thin', color: { rgb: 'D0D0D0' } },
      right: { style: 'thin', color: { rgb: 'D0D0D0' } }
    }
  };

  const borderStyle = {
    border: {
      top: { style: 'thin', color: { rgb: 'E0E0E0' } },
      bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
      left: { style: 'thin', color: { rgb: 'E0E0E0' } },
      right: { style: 'thin', color: { rgb: 'E0E0E0' } }
    }
  };

  // Header cells
  for (let c = 0; c < 2; c += 1) {
    const addr = encode({ r: headerRowIndex, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }

  // Body rows
  for (let r = headerRowIndex + 1; r <= totalRowIndex; r += 1) {
    for (let c = 0; c < 2; c += 1) {
      const addr = encode({ r, c });
      if (!ws[addr]) continue;
      ws[addr].s = {
        ...(ws[addr].s || {}),
        ...borderStyle,
        alignment: { horizontal: c === 1 ? 'right' : 'left', vertical: 'center' },
        font: { sz: 11, color: { rgb: '1F1F1F' } }
      };
      if (c === 1 && typeof ws[addr].v === 'number') {
        ws[addr].t = 'n';
        ws[addr].z = '#,##0';
      }
    }
  }

  // Total row formatting
  const totalText = `${(t ? t('geo.total', { count: totalsValue, value: formattedTotal, total: totalsValue }) : `Total: ${formattedTotal}`)}`;
  const totalAddrLeft = encode({ r: totalRowIndex, c: 0 });
  const totalAddrRight = encode({ r: totalRowIndex, c: 1 });
  ws[totalAddrLeft] = { t: 's', v: totalText };
  ws[totalAddrRight] = { t: 's', v: '' };
  merges.push({ s: { r: totalRowIndex, c: 0 }, e: { r: totalRowIndex, c: 1 } });
  const totalCell = ws[totalAddrLeft];
  totalCell.s = {
    font: { bold: true, sz: 12, color: { rgb: '1F1F1F' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    ...borderStyle
  };

  ws['!cols'] = [
    { wch: 32 },
    { wch: 20 }
  ];

  ws['!merges'] = merges;
  const filterEndRow = headerRowIndex + countryRows.length;
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({ s: { r: headerRowIndex, c: 0 }, e: { r: filterEndRow, c: 1 } })
  };

  try {
    ws['!freeze'] = { xSplit: 0, ySplit: headerRowIndex + 1 };
  } catch {
    /* ignore */
  }

  XLSX.utils.book_append_sheet(wb, ws, 'GEO');
  return wb;
}

export default buildGeoWorkbook;
